import { useState, type FunctionComponent } from "react";
import { InboxOutlined, MessageOutlined, UndoOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Popconfirm, Space, Table, Tag, Tooltip, Typography, message } from "antd";
import dayjs from "dayjs";

import { MessageInContext } from "@fitness-app/data-models/entities/Chat";
import { ExerciseRecordingStatus } from "@fitness-app/data-models/entities/ExerciseRecording";

import VideoThumbnailButton from "~/components/VideoPlayer/VideoThumbnailButton";
import { useChatMessageForm } from "~/modules/Chat/components/ChatNotificationContext/ChatMessageFormContext";
import { useUpdateExerciseRecording } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/TraineeVideoRecordings/useUpdateExerciseRecording";
import {
  recordingPointsAreAutomatic,
  recordingScoresPoints,
  useChallengeRecordingsPage,
  type ChallengeRecordingRow,
  type ChallengeRecordingTask,
  type RecordingStatusFilter,
} from "./useChallengeRecordings";

const PAGE_SIZE = 20;

const statusBadge: Record<string, { status: "default" | "processing" | "success" | "warning"; label: string }> = {
  [ExerciseRecordingStatus.PENDING]: { status: "default", label: "Oczekuje" },
  [ExerciseRecordingStatus.SCHEDULED]: { status: "default", label: "Zaplanowane" },
  [ExerciseRecordingStatus.RECORDED]: { status: "processing", label: "Nagrane" },
  [ExerciseRecordingStatus.RATED]: { status: "success", label: "Ocenione" },
  [ExerciseRecordingStatus.ARCHIVED]: { status: "warning", label: "Zarchiwizowane" },
};

const traineeName = (recording: ChallengeRecordingRow): string => {
  const trainee = recording.trainee;
  if (!trainee) {
    return recording.traineeId;
  }
  return `${trainee.firstName ?? ""} ${trainee.lastName ?? ""}`.trim() || trainee.email || recording.traineeId;
};

type Props = {
  challengeId: string;
  task: ChallengeRecordingTask;
  date: string;
  statusFilter: RecordingStatusFilter;
  search: string;
  onPlay: (url: string) => void;
};

/**
 * One event date's recordings — a page at a time, straight from the server. The revert actions live
 * here because this is where the trainer notices a recording that should not have scored.
 */
const ChallengeRecordingsDatePanel: FunctionComponent<Props> = ({
  challengeId,
  task,
  date,
  statusFilter,
  search,
  onPlay,
}) => {
  const queryClient = useQueryClient();
  const { open } = useChatMessageForm();
  const [page, setPage] = useState(1);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const { deleteExerciseRecordingVideo, archiveExerciseRecording } = useUpdateExerciseRecording();
  const isAutoScored = recordingPointsAreAutomatic(task.verification);

  const { data, isFetching } = useChallengeRecordingsPage({
    challengeId,
    task,
    date,
    statusFilter,
    search,
    page,
    pageSize: PAGE_SIZE,
    enabled: true,
  });

  // The score engine reads the STATUS, not the url — both actions below drop the recording back to
  // rank 0, so the day stops counting. The leaderboard itself is recomputed by the half-hourly
  // `recompute-active-challenges-task` sweep, hence the wording in the confirmations.
  const revert = async (recording: ChallengeRecordingRow, mode: "delete" | "archive") => {
    setPendingId(recording.id);
    try {
      const result =
        mode === "delete" ? await deleteExerciseRecordingVideo(recording) : await archiveExerciseRecording(recording);

      // The shared hook already toasts a failed write — don't claim success on top of it.
      if (result?.error) {
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ["exercise_recordings"] });
      await queryClient.invalidateQueries({ queryKey: ["challenge-leaderboard", challengeId] });
      void message.success(
        mode === "delete"
          ? "Nagranie cofnięte — punkty znikną z rankingu przy najbliższym przeliczeniu (do 30 minut)"
          : "Nagranie zarchiwizowane — punkty znikną z rankingu przy najbliższym przeliczeniu (do 30 minut)",
      );
    } catch (error) {
      void message.error(`Nie udało się cofnąć nagrania: ${(error as Error).message}`);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Table<ChallengeRecordingRow>
      dataSource={data?.rows ?? []}
      rowKey="id"
      size="small"
      scroll={{ x: true }}
      loading={isFetching}
      locale={{ emptyText: "Brak nagrań spełniających filtr" }}
      pagination={{
        current: page,
        pageSize: PAGE_SIZE,
        total: data?.total ?? 0,
        showSizeChanger: false,
        hideOnSinglePage: true,
        showTotal: (total) => `${total} nagrań`,
        onChange: setPage,
      }}
    >
      <Table.Column<ChallengeRecordingRow>
        title="Klientka"
        key="trainee"
        render={(_, recording) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{traineeName(recording)}</Typography.Text>
            <Typography.Text type="secondary">{recording.trainee?.email}</Typography.Text>
          </Space>
        )}
      />
      <Table.Column<ChallengeRecordingRow>
        title="Status"
        key="status"
        render={(_, recording) => {
          const meta = statusBadge[recording.status] ?? { status: "default" as const, label: recording.status };
          return <Badge status={meta.status} text={meta.label} />;
        }}
      />
      <Table.Column<ChallengeRecordingRow>
        title="Punkty"
        key="points"
        render={(_, recording) => {
          if (!recordingScoresPoints(recording.status, task.verification)) {
            return <Typography.Text type="secondary">—</Typography.Text>;
          }
          return (
            <Tooltip
              title={
                isAutoScored
                  ? "Naliczone automatycznie po wysłaniu nagrania. Żeby je odebrać, użyj „Cofnij” albo „Archiwizuj”."
                  : "Naliczone po ocenie trenerki."
              }
            >
              <Tag color="green">+{task.points} pkt</Tag>
            </Tooltip>
          );
        }}
      />
      <Table.Column<ChallengeRecordingRow>
        title="Wysłano"
        key="addedAt"
        render={(_, recording) => {
          const addedAt = recording.metadata?.addedAt;
          if (!addedAt) {
            return <Typography.Text type="secondary">—</Typography.Text>;
          }
          // A slot filled on a different day than it was scheduled for is worth flagging: it still
          // scores that day, and it usually means the client sent one workout into two slots.
          const sentOnOtherDay = !dayjs(addedAt).isSame(dayjs(date), "day");
          return (
            <Space size={4} wrap>
              <Typography.Text>{dayjs(addedAt).format("DD.MM.YYYY HH:mm")}</Typography.Text>
              {sentOnOtherDay ? <Tag color="orange">inny dzień niż termin</Tag> : null}
            </Space>
          );
        }}
      />
      <Table.Column<ChallengeRecordingRow>
        title="Wideo"
        key="video"
        render={(_, recording) =>
          recording.url ? (
            <VideoThumbnailButton
              onClick={() => onPlay(recording.url as string)}
              thumbUrl={recording.metadata?.thumbnailUrl}
              durationSeconds={recording.metadata?.videoProbe?.durationSeconds}
              label="Odtwórz"
            />
          ) : (
            <Typography.Text type="secondary">—</Typography.Text>
          )
        }
      />
      <Table.Column<ChallengeRecordingRow>
        title="Ocena"
        key="rating"
        render={(_, recording) => {
          const canRate =
            recording.status === ExerciseRecordingStatus.RECORDED || recording.status === ExerciseRecordingStatus.RATED;

          if (!canRate) {
            return <Typography.Text type="secondary">—</Typography.Text>;
          }

          const isRated = recording.status === ExerciseRecordingStatus.RATED;

          return (
            <Tooltip
              title={
                isAutoScored
                  ? "Ocena to feedback dla klientki — punkty są już przyznane i ocena ich nie zmienia."
                  : "Ocena przyznaje punkty za ten termin."
              }
            >
              <Button
                type={isRated ? "default" : "primary"}
                size="small"
                icon={<MessageOutlined />}
                onClick={() =>
                  open(
                    {
                      type: MessageInContext.AddRecordingFeedback,
                      metadata: {
                        exerciseName: recording.exercise?.name ?? "",
                        recordingId: recording.id,
                        url: recording.url,
                        workoutId: recording.workoutId,
                        exerciseDate: recording.scheduledAt,
                      },
                    },
                    recording.traineeId,
                  )
                }
              >
                {isRated ? "Zmień ocenę" : "Oceń"}
              </Button>
            </Tooltip>
          );
        }}
      />
      <Table.Column<ChallengeRecordingRow>
        title="Akcje"
        key="actions"
        render={(_, recording) => {
          if (!recording.url) {
            return <Typography.Text type="secondary">—</Typography.Text>;
          }

          const isPending = pendingId === recording.id;

          return (
            <Space size={4} wrap>
              <Popconfirm
                title="Cofnąć to nagranie?"
                description={
                  <div style={{ maxWidth: 320 }}>
                    Wideo zostanie usunięte, a termin wróci do stanu „zaplanowane" — klientka może nagrać go od nowa.
                    Punkty znikną z rankingu przy najbliższym przeliczeniu (do 30 minut).
                  </div>
                }
                okText="Cofnij"
                okButtonProps={{ danger: true }}
                cancelText="Anuluj"
                onConfirm={() => void revert(recording, "delete")}
              >
                <Button size="small" danger icon={<UndoOutlined />} loading={isPending}>
                  Cofnij
                </Button>
              </Popconfirm>
              <Tooltip title="Zachowuje plik jako dowód, ale termin przestaje punktować">
                <Popconfirm
                  title="Zarchiwizować to nagranie?"
                  description={
                    <div style={{ maxWidth: 320 }}>
                      Wideo zostaje, ale termin przestaje się liczyć do punktów. Ranking przeliczy się do 30 minut.
                    </div>
                  }
                  okText="Zarchiwizuj"
                  cancelText="Anuluj"
                  onConfirm={() => void revert(recording, "archive")}
                >
                  <Button size="small" icon={<InboxOutlined />} loading={isPending}>
                    Archiwizuj
                  </Button>
                </Popconfirm>
              </Tooltip>
            </Space>
          );
        }}
      />
    </Table>
  );
};

export default ChallengeRecordingsDatePanel;
